import { mergeWith, set } from '@/framework/utils/fp';
import get, { defaultTo, fromPairs, pick } from '@/utils/lodash-alternatives';

const transformPath = p => {
  const path = defaultTo(p, '');
  return path.includes('components.') && path.includes('.properties')
    ? path
        .replace('components.', 'componentsProperties.')
        .replace('.properties', '')
    : path;
};

const layoutSetupReducer = (_, action) => {
  const { root = null, components = {} } = get(action, 'payload.value');
  const [componentsEntries, componentsPropertiesEntries] = Object.entries(
    components
  ).reduce(
    (
      [componentsList = [], propertiesList = []],
      [id, { properties, ...config }]
    ) => [
      [...componentsList, [id, { ...config, properties: {} }]],
      [...propertiesList, [id, properties]],
    ],
    [[], []]
  );
  return {
    root,
    components: fromPairs(componentsEntries),
    componentsProperties: fromPairs(componentsPropertiesEntries),
  };
};

const layoutSetReducer = (state, action) => {
  const value = get(action, 'payload.value');
  const primitive = value !== Object(value);
  const path = transformPath(get(action, 'payload.path'));
  const keep = transformPath(get(action, 'payload.keep'));
  return set(
    path,
    primitive || Array.isArray(value)
      ? value
      : mergeWith(pick(get(state, path), defaultTo(keep, [])), value),
    state
  );
};

const layoutMergeReducer = (state, action) => {
  const path = transformPath(get(action, 'payload.path'));
  return set(
    path,
    mergeWith(get(state, path), get(action, 'payload.value'), (a, b) =>
      Array.isArray(a) && Array.isArray(b) ? b : undefined
    ),
    state
  );
};

const layoutSetComponentChildrenReducer = (state, action) => {
  const path = get(action, 'payload.path');
  const newChildren = get(action, 'payload.value');
  return set(path, newChildren, state);
};

const layoutSetComponentsMapReducer = (state, action) => {
  const newComponentsMap = JSON.parse(
    JSON.stringify(get(action, 'payload.value'))
  );
  const currentComponents = JSON.parse(JSON.stringify(state.components));
  return set(
    'components',
    { ...currentComponents, ...newComponentsMap },
    state
  );
};

const layoutBatchReducer = (state, { payload = [] }) =>
  payload.reduce(reducer, state);

const defaultReducer = state => state;

const actionReducersMap = {
  LAYOUT_SETUP: layoutSetupReducer,
  LAYOUT_SET: layoutSetReducer,
  LAYOUT_MERGE: layoutMergeReducer,
  LAYOUT_BATCH: layoutBatchReducer,
  LAYOUT_SET_COMPONENT_CHILDREN: layoutSetComponentChildrenReducer,
  LAYOUT_SET_COMPONENTS_MAP: layoutSetComponentsMapReducer,
};

const reducer = (state = {}, action) => {
  const actionReducer = actionReducersMap[action.type] || defaultReducer;
  return actionReducer(state, action);
};

export default reducer;
